// =============================================================================
// AI Chat tab — conversational interface (R39-R41, R53).
//   R39: Ask status questions in plain English
//   R40: Summarise site status on request
//   R53: Feedback capture on AI answers
//
// One of two sub-tabs on the AI Portal page (see pages/AskPortal.tsx). The page
// shell owns the header + tab strip; this component is the chat body only.
// =============================================================================
import { useEffect, useRef, useState } from 'react'
import { Send, ThumbsUp, ThumbsDown, Bot, User, RotateCcw } from 'lucide-react'
import ReactMarkdown from 'react-markdown'
import remarkGfm from 'remark-gfm'
import { api, ApiError } from '../../lib/api'
import { Card, Button } from '../../components/ui'
import { useAskPortalSlice, updateChatMessages } from '../../store/usePageSlices'

// Compact element styling for rendered assistant markdown — enough that lists/bold/tables
// read cleanly inside the chat bubble, without pulling in a full typography theme. No `img`
// or raw-HTML handling: react-markdown ignores HTML by default (no rehype-raw = XSS-safe).
const MD_COMPONENTS = {
  p: (props: React.HTMLAttributes<HTMLParagraphElement>) => <p className="mb-2 last:mb-0" {...props} />,
  ul: (props: React.HTMLAttributes<HTMLUListElement>) => <ul className="mb-2 list-disc pl-5 last:mb-0 space-y-0.5" {...props} />,
  ol: (props: React.HTMLAttributes<HTMLOListElement>) => <ol className="mb-2 list-decimal pl-5 last:mb-0 space-y-0.5" {...props} />,
  li: (props: React.HTMLAttributes<HTMLLIElement>) => <li className="leading-relaxed" {...props} />,
  strong: (props: React.HTMLAttributes<HTMLElement>) => <strong className="font-semibold" {...props} />,
  a: (props: React.AnchorHTMLAttributes<HTMLAnchorElement>) => <a className="text-brand-600 underline" target="_blank" rel="noreferrer" {...props} />,
  // Inline code → pill; fenced block code → plain (the <pre> below provides the frame). `inline`
  // is passed by react-markdown; branch on it so multi-line blocks don't get inline-pill padding.
  code: ({ inline, ...props }: React.HTMLAttributes<HTMLElement> & { inline?: boolean }) =>
    inline
      ? <code className="rounded bg-slate-200/70 px-1 py-0.5 font-mono text-[12px]" {...props} />
      : <code className="font-mono text-[12px]" {...props} />,
  pre: (props: React.HTMLAttributes<HTMLPreElement>) => <pre className="mb-2 overflow-x-auto rounded bg-slate-200/70 p-2 last:mb-0" {...props} />,
  table: (props: React.HTMLAttributes<HTMLTableElement>) => <div className="mb-2 overflow-auto last:mb-0"><table className="w-full border-collapse text-[12px]" {...props} /></div>,
  th: (props: React.HTMLAttributes<HTMLTableCellElement>) => <th className="border border-line bg-slate-100 px-2 py-1 text-left font-semibold" {...props} />,
  td: (props: React.HTMLAttributes<HTMLTableCellElement>) => <td className="border border-line px-2 py-1" {...props} />,
}

const SUGGESTIONS = [
  'How many sites have their migration date set?',
  'Which sites are at risk and why?',
  'What is the current run-rate?',
  'Show me sites with delayed circuits',
  'What changed this week?',
  'Is the pipeline healthy enough to sustain delivery?',
  'Are there any data quality issues?',
  'Which sites are migrating next week with no SNOW CR?',
]

export function AskPortalChat() {
  // Conversation persists in the page store so it survives navigating away and back.
  // `input`/`loading`/`agentEnabled` stay local — truly transient per-visit state.
  const messages = useAskPortalSlice((s) => s.messages)
  const [input, setInput] = useState('')
  const [loading, setLoading] = useState(false)
  const [agentEnabled, setAgentEnabled] = useState<boolean | null>(null)
  const endRef = useRef<HTMLDivElement>(null)
  const inputRef = useRef<HTMLInputElement>(null)

  // Check if agent is configured
  useEffect(() => {
    api.agentConfig().then((c) => setAgentEnabled(c.enabled)).catch(() => setAgentEnabled(false))
  }, [])

  async function send(text?: string) {
    const msg = (text || input).trim()
    if (!msg || loading) return
    setInput('')

    // Append via functional updates over LIVE store state (not a render snapshot), so a
    // feedback click or another mutation landing during the await isn't clobbered.
    const history = messages.map((m) => ({ role: m.role, content: m.content }))
    updateChatMessages((prev) => [...prev, { id: `u-${Date.now()}`, role: 'user', content: msg }])
    setLoading(true)

    try {
      const res = await api.agentChat({ message: msg, history })
      updateChatMessages((prev) => [...prev, { id: `a-${Date.now()}`, role: 'assistant', content: res.reply, tools: res.used_tools }])
    } catch (e) {
      const errMsg = e instanceof ApiError ? e.message : 'Failed to get a response. Please try again.'
      updateChatMessages((prev) => [...prev, { id: `e-${Date.now()}`, role: 'assistant', content: errMsg }])
    } finally {
      setLoading(false)
      // Deferred so it runs after the re-render re-enables the input (focus() no-ops on a
      // disabled element) — keeps the cursor ready for the next question, and scrolls to end.
      setTimeout(() => { endRef.current?.scrollIntoView({ behavior: 'smooth' }); inputRef.current?.focus() }, 50)
    }
  }

  // Reset the conversation — clears the persisted message buffer and the input box.
  // Guarded while a send is in flight: resetting mid-request would let the awaited
  // reply append onto the emptied buffer, leaving an orphaned assistant bubble.
  function resetConversation() {
    if (loading) return
    updateChatMessages(() => [])
    setInput('')
    inputRef.current?.focus()
  }

  async function sendFeedback(msgId: string, useful: boolean) {
    updateChatMessages((prev) => prev.map((m) => m.id === msgId ? { ...m, feedback: useful } : m))
    await api.feedback({ feedback_type: 'chat', reference_id: msgId, useful }).catch(() => {})
  }

  return (
    <div className="flex min-h-0 flex-1 flex-col">
      {agentEnabled === false && (
        <Card className="mb-4 p-4">
          <p className="text-[12px] text-rag-amber">AI is not configured in this demo. Set GCP Vertex/ADC to enable. Everything else in the portal works without it.</p>
        </Card>
      )}

      {/* Reset — only shown once a conversation exists (nothing to reset otherwise). */}
      {messages.length > 0 && (
        <div className="mb-2 flex flex-shrink-0 justify-end">
          <button onClick={resetConversation} disabled={loading}
            className="inline-flex items-center gap-1.5 rounded-md border border-line px-2.5 py-1 text-[12px] font-medium text-muted hover:border-brand-200 hover:text-ink disabled:opacity-50 disabled:pointer-events-none">
            <RotateCcw className="h-3.5 w-3.5" /> Reset conversation
          </button>
        </div>
      )}

      {/* Message area — the bordered frame clips; only the inner list scrolls, so the
          prompt bar below stays pinned and the page itself never scrolls. */}
      <div className="flex min-h-0 flex-1 flex-col overflow-hidden rounded-card border border-line bg-surface">
        <div className="min-h-0 flex-1 overflow-auto px-4 py-4 space-y-4">
          {messages.length === 0 && (
            <div className="flex flex-col items-center gap-4 py-12">
              <Bot className="h-10 w-10 text-brand-200" />
              <p className="text-sm text-muted">Ask anything about the programme. Figures come from live data.</p>
              <div className="flex flex-wrap justify-center gap-2">
                {SUGGESTIONS.map((s) => (
                  <button key={s} onClick={() => send(s)} disabled={loading}
                    className="rounded-full border border-line bg-canvas px-3 py-1.5 text-[12px] text-muted hover:border-brand-600 hover:text-brand-600 disabled:opacity-50">
                    {s}
                  </button>
                ))}
              </div>
            </div>
          )}

          {messages.map((m) => (
            <div key={m.id} className={`flex gap-3 ${m.role === 'user' ? 'justify-end' : ''}`}>
              {m.role === 'assistant' && <Bot className="mt-1 h-5 w-5 flex-shrink-0 text-brand-600" />}
              <div className={`max-w-[80%] rounded-lg px-3 py-2 text-[13px] leading-relaxed ${
                m.role === 'user' ? 'bg-brand-600 text-white' : 'bg-slate-50 text-ink border border-line'
              }`}>
                {m.role === 'assistant' ? (
                  <div className="[&>*:first-child]:mt-0 [&>*:last-child]:mb-0">
                    <ReactMarkdown remarkPlugins={[remarkGfm]} components={MD_COMPONENTS}>{m.content}</ReactMarkdown>
                  </div>
                ) : (
                  <p className="whitespace-pre-wrap">{m.content}</p>
                )}
                {m.role === 'assistant' && m.tools && m.tools.length > 0 && (
                  <p className="mt-1 text-[10px] text-muted">Tools used: {m.tools.join(', ')}</p>
                )}
                {/* Feedback on assistant messages (R53) */}
                {m.role === 'assistant' && (
                  <div className="mt-2 flex items-center gap-2 border-t border-line/60 pt-1.5">
                    {m.feedback == null ? (
                      <>
                        <span className="text-[10px] text-muted">Helpful?</span>
                        <button onClick={() => sendFeedback(m.id, true)} className="rounded border border-line p-0.5 text-muted hover:text-rag-green"><ThumbsUp className="h-3 w-3" /></button>
                        <button onClick={() => sendFeedback(m.id, false)} className="rounded border border-line p-0.5 text-muted hover:text-rag-red"><ThumbsDown className="h-3 w-3" /></button>
                      </>
                    ) : <span className="text-[10px] text-muted">✓ Feedback sent</span>}
                  </div>
                )}
              </div>
              {m.role === 'user' && <User className="mt-1 h-5 w-5 flex-shrink-0 text-muted" />}
            </div>
          ))}

          {loading && (
            <div className="flex gap-3">
              <Bot className="mt-1 h-5 w-5 text-brand-600" />
              <div className="rounded-lg border border-line bg-slate-50 px-3 py-2">
                <span className="inline-flex gap-1"><span className="animate-bounce text-muted">●</span><span className="animate-bounce text-muted" style={{ animationDelay: '0.1s' }}>●</span><span className="animate-bounce text-muted" style={{ animationDelay: '0.2s' }}>●</span></span>
              </div>
            </div>
          )}
          <div ref={endRef} />
        </div>

        {/* Input */}
        <form onSubmit={(e) => { e.preventDefault(); send() }} className="border-t border-line px-4 py-3">
          <div className="flex gap-2">
            <input ref={inputRef} type="text" value={input} onChange={(e) => setInput(e.target.value)} maxLength={2000}
              placeholder="Ask about delivery progress, risk, or status…" disabled={loading}
              className="min-w-0 flex-1 rounded-md border border-line bg-canvas px-3 py-2 text-[13px] outline-none placeholder:text-muted focus:border-brand-600" />
            <Button type="submit" variant="primary" disabled={loading || !input.trim()}>
              <Send className="h-4 w-4" />
            </Button>
          </div>
          <p className="mt-1.5 text-[10px] text-muted">All answers come from live programme data via read-only tools. The AI cannot change any data.</p>
        </form>
      </div>
    </div>
  )
}
